import { css } from 'lit';
import { hostReset, focusRing } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  focusRing('.uwc-slider__handle', '--uwc-color-primary', '#6366f1'),
  css`
    :host {
      display: block;
    }

    /* ── Fluid ─────────────────────────────────────────────────────────── */
    :host([fluid]) {
      width: 100%;
    }

    /* ── Container ───────────────────────────────────────────────────────── */
    .uwc-slider {
      position:    relative;
      padding:     ${t.space3} 0;
      user-select: none;
      touch-action: none;
    }

    /* Vertical container */
    .uwc-slider--vertical {
      padding:        0 ${t.space3};
      display:        inline-flex;
      align-items:    stretch;
      height:         var(--uwc-slider-vertical-height, 200px);
    }

    :host([fluid]) .uwc-slider--vertical {
      height: var(--uwc-slider-vertical-height, 200px);
    }

    /* ── Track ────────────────────────────────────────────────────────────── */
    .uwc-slider__track {
      position:      relative;
      height:        var(--uwc-slider-height, calc(var(--uwc-font-size-md, 0.875rem) * 0.29));
      background:    var(--uwc-slider-bg, ${t.borderControl});
      border-radius: var(--uwc-slider-border-radius, ${t.radiusFull});
      cursor:        pointer;
    }

    .uwc-slider--vertical .uwc-slider__track {
      width:  var(--uwc-slider-height, calc(var(--uwc-font-size-md, 0.875rem) * 0.29));
      height: 100%;
    }

    /* ── Range fill ─────────────────────────────────────────────────────────── */
    .uwc-slider__range {
      position:       absolute;
      top:            0;
      bottom:         0;
      background:     var(--uwc-slider-range-bg, var(--uwc-color-primary, ${t.primary}));
      border-radius:  inherit;
      pointer-events: none;
      transition:     background ${t.durationBase};
    }

    .uwc-slider--vertical .uwc-slider__range {
      top:    auto;
      left:   0;
      right:  0;
      /* bottom / top driven by styleMap */
    }

    /* ── Handle ─────────────────────────────────────────────────────────────── */
    .uwc-slider__handle {
      position:      absolute;
      top:           50%;
      /* Handle/track sizes derive from the global font token (×1.43 / ×0.29 ≈
         the old 20px / 4px at the 0.875rem default) so the slider rescales with
         the size/font presets. Themes can still pin exact px via
         --uwc-slider-handle-size / --uwc-slider-height. */
      width:         var(--uwc-slider-handle-size, calc(var(--uwc-font-size-md, 0.875rem) * 1.43));
      height:        var(--uwc-slider-handle-size, calc(var(--uwc-font-size-md, 0.875rem) * 1.43));
      transform:     translate(-50%, -50%);
      border-radius: 50%;
      background:    var(--uwc-slider-handle-bg, ${t.surface});
      border:        2px solid var(--uwc-slider-handle-border-color,
                       var(--uwc-color-primary, ${t.primary}));
      box-shadow:    var(--uwc-slider-handle-shadow, ${t.shadowMd});
      cursor:        grab;
      outline:       none;
      z-index:       1;
      transition:
        transform    ${t.durationFast} ease,
        border-color ${t.durationBase},
        box-shadow   ${t.durationBase};
    }

    .uwc-slider__handle:hover {
      transform:    translate(-50%, -50%) scale(1.2);
      border-color: var(--uwc-slider-handle-hover-border-color,
                      color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 80%, black));
    }

    .uwc-slider__handle.is-active {
      cursor:     grabbing;
      transform:  translate(-50%, -50%) scale(1.25);
      box-shadow:
        var(--uwc-slider-handle-shadow, ${t.shadowMd}),
        0 0 0 5px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 18%, transparent);
    }

    /* Vertical handle overrides */
    .uwc-slider--vertical .uwc-slider__handle {
      top:       auto;
      left:      50%;
      transform: translate(-50%, 50%);
    }
    .uwc-slider--vertical .uwc-slider__handle:hover {
      transform: translate(-50%, 50%) scale(1.2);
    }
    .uwc-slider--vertical .uwc-slider__handle.is-active {
      transform: translate(-50%, 50%) scale(1.25);
    }

    /* ── Disabled ────────────────────────────────────────────────────────────── */
    :host([disabled]) .uwc-slider {
      opacity:        0.5;
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-slider__handle { transition: none; }
      .uwc-slider__range  { transition: none; }
    }
  `,
];
